import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Award } from "lucide-react";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import type { OwnedKinds } from "@/shared/organizations/owned-kinds";
import { Button } from "@/shared/components/ui/button";
import { FoundingPartnerBadge } from "./FoundingPartnerBadge";
import { ConvertFoundingPartnerDialog } from "./ConvertFoundingPartnerDialog";
import { OwnerFoundingPropertiesPanel } from "./OwnerFoundingPropertiesPanel";
import { useFoundingAccount } from "./use-founding-account";

interface FoundingPartnerSectionProps {
  readonly userId: string;
  /** What the account owns; replaces the legacy role label as the eligibility gate. */
  readonly ownedKinds: OwnedKinds;
}

/**
 * Founding Partner card on the user-detail page (ADR-0010 §9). Shows the
 * account's FP status, and for a host the grandfather line + founding-property
 * roll-up and the embedded per-host assignment panel. The convert/remove
 * button is ADMIN only; FINANCE reads. Renders nothing for an account that
 * owns no venue or property organization.
 */
export function FoundingPartnerSection({ userId, ownedKinds }: FoundingPartnerSectionProps) {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const viewerCanRead =
    currentUser?.role === Role.WANDERFIT_ADMIN || currentUser?.role === Role.WANDERFIT_FINANCE;
  const canManage = currentUser?.role === Role.WANDERFIT_ADMIN;
  const isHost = ownedKinds.properties;

  const [convertOpen, setConvertOpen] = useState(false);

  const { data: account } = useFoundingAccount(userId, viewerCanRead);

  // Only accounts that own a property or venue organization can be Founding Partners.
  if (!ownedKinds.properties && !ownedKinds.venues) return null;

  const isPartner = account?.foundingPartner ?? false;

  return (
    <section
      data-testid="user-detail-founding"
      className="space-y-4 rounded-lg border border-border bg-card p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="brand-eyebrow flex items-center gap-2">
            <Award className="h-4 w-4" aria-hidden="true" />
            {t("founding.partner.sectionTitle")}
          </h2>
          <div className="flex items-center gap-2">
            {isPartner ? (
              <FoundingPartnerBadge active testId="founding-section-partner-badge" />
            ) : (
              <p
                data-testid="founding-section-not-partner"
                className="font-sans text-sm text-muted-foreground"
              >
                {t("founding.partner.notPartner")}
              </p>
            )}
          </div>
        </div>
        {canManage && (
          <Button
            type="button"
            variant={isPartner ? "outline" : "default"}
            data-testid="open-convert-founding-partner"
            onClick={() => setConvertOpen(true)}
          >
            {isPartner ? t("founding.convert.removeConfirm") : t("founding.convert.convertConfirm")}
          </Button>
        )}
      </div>

      {isHost && account && (
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("founding.account.grandfather")}
            </dt>
            <dd
              data-testid="founding-section-grandfather"
              className="mt-1 font-sans text-sm text-foreground"
            >
              {account.grandfatherNewProperties
                ? t("founding.account.grandfatherOn")
                : t("founding.account.grandfatherOff")}
            </dd>
          </div>
          <div>
            <dt className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("founding.account.foundingProperties")}
            </dt>
            <dd
              data-testid="founding-section-rollup"
              className="mt-1 font-sans text-sm text-foreground"
            >
              {t("founding.account.rollup", {
                active: account.activeFoundingPropertyCount,
                total: account.foundingPropertyCount,
              })}
            </dd>
          </div>
        </dl>
      )}

      {isHost && <OwnerFoundingPropertiesPanel userId={userId} />}

      {canManage && (
        <ConvertFoundingPartnerDialog
          userId={userId}
          ownsProperties={ownedKinds.properties}
          currentlyPartner={isPartner}
          open={convertOpen}
          onOpenChange={setConvertOpen}
          onDone={() => setConvertOpen(false)}
        />
      )}
    </section>
  );
}
